/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE.txt file for details)
*/

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from "@mui/material";
import { buildExperimentTimelineOverview } from "../utils/timelineOverview";
import { buildSection30Summary } from "../utils/section30Summary";
import { getStrainColumns, getGroupCount } from "./Section29Form";

const SectionHeading = ({ children }) => (
  <Typography
    sx={{
      mt: 3,
      mb: 1,
      fontWeight: "bold",
      textDecoration: "underline",
      fontFamily: '"Times New Roman", serif',
      fontSize: "1rem",
    }}
  >
    {children}
  </Typography>
);

const DetailLine = ({ label, value }) => {
  if (!value || !String(value).trim()) return null;
  return (
    <Typography sx={{ fontFamily: '"Times New Roman", serif', fontSize: "0.9rem", mb: 0.5 }}>
      <strong>{label}:</strong>{" "}
      <Box component="span" sx={{ color: "#cc0000", fontWeight: "bold" }}>
        {value}
      </Box>
    </Typography>
  );
};

const SectionIntro = ({ children }) => (
  <Typography
    sx={{
      fontFamily: '"Times New Roman", serif',
      fontSize: "0.88rem",
      color: "#444",
      mb: 1.5,
      lineHeight: 1.6,
    }}
  >
    {children}
  </Typography>
);

const formatInterventionLine = (interv, index, i, repeatText = "") => {
  const isTypeOther = interv.interventionType === "Other";
  const type = isTypeOther && interv.interventionTypeOther
    ? interv.interventionTypeOther
    : interv.interventionType;

  const method = interv.interventionMethod?.toLowerCase().includes("other") &&
    interv.interventionMethodOther
    ? interv.interventionMethodOther
    : interv.interventionMethod;

  const base = `- Experiment ${index + 1}: Intervention ${i + 1}: Type: ${type}`;

  return isTypeOther ? `${base}${repeatText}` : `${base}, Method: ${method}${repeatText}`;
};

const buildRepeatText = (interv) => {
  if (interv.repeatedIntervention === "specific_days" && interv.specificDays) {
    return `, on days: ${interv.specificDays}`;
  }
  if (interv.repeatedIntervention === "every_x_days") {
    const x = interv.xDays || "X";
    const y = interv.yTimes || "Y";
    return `, repeated every ${x} days, ${y} times`;
  }
  if (interv.repeatedIntervention === "every_day") {
    const x = interv.xDays || "X";
    return `, repeated every day for ${x} days`;
  }
  return "";
};

function DocPreview({ formData, projectName, timelineImages = [] }) {
  const [imageUrls, setImageUrls] = useState([]);

  // Convert Uint8Array images to object URLs (cleaned up on unmount / change)
  useEffect(() => {
    const urls = timelineImages.map((img) => {
      if (img && img.data) {
        return URL.createObjectURL(new Blob([img.data], { type: "image/png" }));
      }
      return null;
    });
    setImageUrls(urls);
    return () => {
      urls.forEach((u) => u && URL.revokeObjectURL(u));
    };
  }, [timelineImages]);

  const strainColumns = getStrainColumns(formData);

  // Section 29 grand totals per column
  const grandTotals = strainColumns.map(({ id }) =>
    formData.reduce(
      (s, exp) => s + (exp.groups || []).reduce((gs, g) => gs + (parseInt(getGroupCount(g, id), 10) || 0), 0),
      0
    )
  );
  const grandTotalAll = grandTotals.reduce((a, b) => a + b, 0);

  // Severity totals per column across all experiments
  const SD_KEYS = ["sd0", "sd1", "sd2", "sd3"];
  const severityTotals = SD_KEYS.reduce((acc, key) => {
    acc[key] = strainColumns.map(({ id }) =>
      formData.reduce((s, exp) => s + (parseInt(((exp.severityDistribution || {})[key] || {})[id], 10) || 0), 0)
    );
    return acc;
  }, {});

  const cellStyle = { fontFamily: '"Times New Roman", serif', fontSize: "0.85rem" };
  const headerCellStyle = { ...cellStyle, fontWeight: "bold", backgroundColor: "#f5f5f5" };
  const pinkStyle = { backgroundColor: "#fce4ec" };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        width: "100%",
        maxWidth: 820,
        margin: "0 auto",
        px: { xs: 3, sm: 6 },
        py: 5,
        boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
        fontFamily: '"Times New Roman", serif',
        fontSize: "0.95rem",
        lineHeight: 1.7,
      }}
    >
      {/* Header */}
      <Typography
        variant="h5"
        sx={{ textAlign: "center", mb: 0.5, fontWeight: "bold", fontFamily: "inherit" }}
      >
        Animal Experiment Authorization Request
      </Typography>
      <Typography sx={{ textAlign: "center", color: "#555", mb: 3, fontFamily: "inherit" }}>
        Project:{" "}
        <Box component="span" sx={{ fontWeight: "bold" }}>
          {projectName}
        </Box>
      </Typography>
      <Divider />

      <SectionHeading>Section 23: Course of the experiments - Schematic representation</SectionHeading>
      <SectionIntro>
        Description of the course of the experiment from a temporal perspective. Representation of the course of the
        experiment or individual steps such as a flow chart, workflow diagram, or table. To include: animal model,
        animal groups, and the overall duration of the experiment for each group.
      </SectionIntro>

      {formData.map((exp, i) => {
        const label = exp.experimentObjective || `Experiment ${i + 1}`;
        const url = imageUrls[i];
        const overview = buildExperimentTimelineOverview(exp, i);
        return (
          <Box key={i} sx={{ mb: 3 }}>
            <Typography
              sx={{ fontWeight: "bold", color: "#cc0044", mb: 1, fontFamily: "inherit" }}
            >
              {label}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                gap: 1.2,
                mb: 1.5,
              }}
            >
              <Box sx={{ border: "1px solid #d9e4ea", borderRadius: 1.5, p: 1.2, backgroundColor: "#f7fbfd" }}>
                <Typography sx={{ fontSize: "0.72rem", textTransform: "uppercase", color: "#5d6e78" }}>Duration</Typography>
                <Typography sx={{ fontWeight: "bold", fontSize: "0.92rem" }}>{overview.durationDays || 0} days</Typography>
              </Box>
              <Box sx={{ border: "1px solid #d9e4ea", borderRadius: 1.5, p: 1.2, backgroundColor: "#fcfcfc" }}>
                <Typography sx={{ fontSize: "0.72rem", textTransform: "uppercase", color: "#5d6e78" }}>Cohorts</Typography>
                <Typography sx={{ fontWeight: "bold", fontSize: "0.92rem" }}>{overview.cohorts || "Not specified"}</Typography>
              </Box>
              <Box sx={{ border: "1px solid #d9e4ea", borderRadius: 1.5, p: 1.2, backgroundColor: "#fffaf3" }}>
                <Typography sx={{ fontSize: "0.72rem", textTransform: "uppercase", color: "#7a6842" }}>Repetitions</Typography>
                <Typography sx={{ fontWeight: "bold", fontSize: "0.92rem" }}>{overview.repetitions || "Not specified"}</Typography>
              </Box>
            </Box>
            {url ? (
              <Box
                component="img"
                src={url}
                alt={`Timeline ${i + 1}`}
                sx={{
                  maxWidth: "100%",
                  height: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  display: "block",
                }}
              />
            ) : (
              <Box
                sx={{
                  height: 140,
                  border: "1px dashed #c0cdd6",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#94a3b8",
                  fontStyle: "italic",
                  fontSize: "0.85rem",
                  backgroundColor: "#f8fafc",
                }}
              >
                Timeline image loading…
              </Box>
            )}
            {overview.legendEntries.length > 0 && (
              <Box sx={{ mt: 1.5, mb: 1.5 }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "0.86rem", mb: 0.8, fontFamily: "inherit" }}>
                  Legend
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
                  {overview.legendEntries.map((entry) => (
                    <Box
                      key={entry.label}
                      sx={{
                        border: "1px solid #e0e6ea",
                        borderRadius: 1.2,
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {entry.icon ? (
                        <Box component="img" src={entry.icon} alt={entry.label} sx={{ width: 20, height: 20, flexShrink: 0 }} />
                      ) : (
                        <Box sx={{ px: 1, py: 0.3, borderRadius: 999, backgroundColor: "#dff1fb", fontSize: "0.72rem", fontWeight: "bold", color: "#24404f", flexShrink: 0 }}>
                          A
                        </Box>
                      )}
                      <Typography sx={{ fontSize: "0.84rem", fontFamily: "inherit", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{entry.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {overview.keySteps.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "0.86rem", mb: 0.6, fontFamily: "inherit" }}>
                  Key timeline steps
                </Typography>
                {overview.keySteps.slice(0, 8).map((step, stepIndex) => (
                  <Typography key={`${step.day}-${step.label}-${stepIndex}`} sx={{ fontFamily: '"Times New Roman", serif', fontSize: "0.88rem", mb: 0.35 }}>
                    <strong>{`Day ${step.day}`}</strong>: {step.label}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        );
      })}

      <SectionHeading>Section 24: Preparation of animals for the experiment</SectionHeading>
      <SectionIntro>
        Assessment of the preparation of animals for the experiment: description of screening examination, preparation
        to the experimental conditions, method of marking or identification, and combination with the method of
        genotyping.
      </SectionIntro>
      {formData.map((exp, i) => {
        const prepLabels = {
          special_diet: "Special diet",
          food_restriction: "Food restriction",
          water_restriction: "Water restriction",
          antibiotics_treatment: "Antibiotics treatment in water",
          tamoxifen_treatment: "Tamoxifen treatment in food/gels",
          other_preparation: "Other special preparation",
        };
        const parts = [];
        const acclimDays = parseInt(exp.acclimatationDays || "0", 10);
        if (exp.acclimatation === "yes" && acclimDays > 0) {
          parts.push(`Acclimatation: ${acclimDays} days`);
        }
        const prepDays = parseInt(exp.preparationDays || "0", 10);
        if (exp.preparation && exp.preparation !== "no_preparation" && prepDays > 0) {
          parts.push(`${prepLabels[exp.preparation] || "Special preparation"}: ${prepDays} days`);
        }
        const value = parts.length > 0 ? parts.join("; ") : "No acclimatation or special preparation.";
        return (
          <DetailLine
            key={`section24-${i}`}
            label={exp.experimentObjective || `Experiment ${i + 1}`}
            value={value}
          />
        );
      })}

      <SectionHeading>Section 25: Procedures / manipulations on the animal</SectionHeading>
      <SectionIntro>
        Indicate the details of the various manipulations or interventions on the animal.
      </SectionIntro>
      {formData.map((exp, index) => {
        const overview = buildExperimentTimelineOverview(exp, index);
        const hasEntries = overview.procedureEntries.length > 0;
        const hasNotes = exp.section25Notes && String(exp.section25Notes).trim();
        if (!hasEntries && !hasNotes) return null;

        return (
          <Box key={`section25-exp-${index}`} sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: "bold", color: "#cc0044", mb: 0.8, fontFamily: "inherit" }}>
              {overview.experimentLabel}
            </Typography>
            {overview.procedureEntries.map((entry) => (
              <Box key={`section25-${index}-${entry.index}`} sx={{ border: "1px solid #e3e7ea", borderRadius: 1.2, p: 1.2, mb: 1 }}>
                <Typography sx={{ fontFamily: '"Times New Roman", serif', fontSize: "0.9rem", fontWeight: "bold", mb: 0.4 }}>
                  {`Procedure ${entry.index + 1}: ${entry.typeLabel}`}
                </Typography>
                <Typography sx={{ fontFamily: '"Times New Roman", serif', fontSize: "0.88rem", mb: 0.25 }}>
                  <strong>Method:</strong> {entry.methodLabel}
                </Typography>
                <Typography sx={{ fontFamily: '"Times New Roman", serif', fontSize: "0.88rem", mb: 0.25 }}>
                  <strong>Timing:</strong> {entry.scheduleLabel}
                </Typography>
                <Typography sx={{ fontFamily: '"Times New Roman", serif', fontSize: "0.88rem" }}>
                  <strong>Timeline days:</strong> {entry.dayLabel}
                </Typography>
              </Box>
            ))}
            {hasNotes && (
              <Box sx={{ mt: hasEntries ? 1 : 0 }}>
                <Typography sx={{ fontFamily: '"Times New Roman", serif', fontSize: "0.88rem", fontWeight: "bold", mb: 0.3 }}>
                  Additional procedure notes:
                </Typography>
                <Typography sx={{ fontFamily: '"Times New Roman", serif', fontSize: "0.88rem", whiteSpace: "pre-wrap" }}>
                  {exp.section25Notes}
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}

      <SectionHeading>Section 26: Anaesthesia and / or analgesia</SectionHeading>
      <SectionIntro>
        Indicate the medications to be used, the dose and volume, the intended routes of administration, and consider
        the need of continuing administration.
      </SectionIntro>
      {formData.flatMap((exp, index) =>
        (exp.interventions || []).map((interv, i) => (
          <Typography key={`section26-${index}-${i}`} sx={{ fontFamily: '"Times New Roman", serif', fontSize: "0.9rem", mb: 0.5 }}>
            {formatInterventionLine(interv, index, i, buildRepeatText(interv))}
          </Typography>
        ))
      )}

      <SectionHeading>Section 27: Methods of euthanasia</SectionHeading>
      <SectionIntro>
        Indicate the method of euthanasia, stating the substance used, the dose and route of administration, and the
        procedure to ensure the death of the animal.
      </SectionIntro>
      {formData.map((exp, index) => {
        const euthanasiaInterventions = (exp.interventions || []).filter((interv) =>
          ["Euthanasia", "Perfusion, fixation and euthanasia", "Blood collection and euthanasia"].includes(interv.interventionMethod)
        );
        if (euthanasiaInterventions.length === 0) return null;
        const expLabel = exp.experimentObjective || `Experiment ${index + 1}`;
        return (
          <Box key={`section27-${index}`} sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: "bold", color: "#cc0044", mb: 0.4, fontFamily: "inherit" }}>
              {expLabel}
            </Typography>
            {euthanasiaInterventions.map((interv, i) => (
              <Typography key={i} sx={{ fontFamily: '"Times New Roman", serif', fontSize: "0.9rem", mb: 0.3 }}>
                {`Method of euthanasia: ${interv.interventionMethod}`}
              </Typography>
            ))}
          </Box>
        );
      })}

      <SectionHeading>Section 29: Experimental set-up and study design</SectionHeading>

      <SectionIntro>
        Description of the study design and planning: number of animals per experiment or series of experiments,
        number of groups, and number of animals per group. Include a description of each group. Specific
        considerations include allocation concealment or randomization, blinding, sample size calculation rationale,
        in- and exclusion criteria, definition of primary outcome variables, and the statistical analysis plan.
      </SectionIntro>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, borderRight: "1px solid #ddd" }}>Objective</TableCell>
              <TableCell sx={{ ...headerCellStyle, borderRight: "1px solid #ddd" }}>Experiment</TableCell>
              <TableCell sx={{ ...headerCellStyle, borderRight: "1px solid #ddd" }}>Groups</TableCell>
              {strainColumns.map((col) => (
                <TableCell key={col.id} align="center" sx={{ ...headerCellStyle, borderRight: "1px solid #ddd" }}>{col.label}</TableCell>
              ))}
              <TableCell align="center" sx={headerCellStyle}>Total</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {formData.map((exp, expIndex) => {
              const objectiveLabel = exp.objectiveLabel || `Objective ${expIndex + 1}`;
              const objectiveRowCount = formData
                .filter((item, idx) => (item.objectiveLabel || `Objective ${idx + 1}`) === objectiveLabel)
                .reduce((sum, item) => {
                  const groupCount = item.groups?.length || 0;
                  return sum + (groupCount > 0 ? groupCount : 1);
                }, 0);
              const firstObjectiveIndex = formData.findIndex(
                (item, idx) => (item.objectiveLabel || `Objective ${idx + 1}`) === objectiveLabel
              );
              const groups = exp.groups || [];
              const expLabel = exp.experimentObjective || `Experiment ${expIndex + 1}`;

              if (groups.length === 0) {
                return (
                  <TableRow key={expIndex}>
                    {expIndex === firstObjectiveIndex && (
                      <TableCell
                        rowSpan={objectiveRowCount}
                        sx={{ ...cellStyle, color: "#cc0044", fontWeight: "bold", verticalAlign: "top", borderRight: "1px solid #ddd" }}
                      >
                        {objectiveLabel}
                      </TableCell>
                    )}
                    <TableCell sx={{ ...cellStyle, color: "#cc0044", fontWeight: "bold", borderRight: "1px solid #ddd" }}>
                      {expLabel}
                    </TableCell>
                    <TableCell
                      colSpan={2 + strainColumns.length}
                      sx={{ ...cellStyle, fontStyle: "italic", color: "#999" }}
                    >
                      No groups defined
                    </TableCell>
                    <TableCell />
                  </TableRow>
                );
              }

              return groups.map((group, gi) => {
                const colCounts = strainColumns.map(({ id }) => parseInt(getGroupCount(group, id), 10) || 0);
                const rowTotal = colCounts.reduce((a, b) => a + b, 0);
                return (
                  <TableRow key={`${expIndex}-${gi}`}>
                    {gi === 0 && expIndex === firstObjectiveIndex && (
                      <TableCell
                        rowSpan={objectiveRowCount}
                        sx={{ ...cellStyle, color: "#cc0044", fontWeight: "bold", verticalAlign: "top", borderRight: "1px solid #ddd" }}
                      >
                        {objectiveLabel}
                      </TableCell>
                    )}
                    {gi === 0 && (
                      <TableCell
                        rowSpan={groups.length}
                        sx={{ ...cellStyle, color: "#cc0044", fontWeight: "bold", verticalAlign: "top", borderRight: "1px solid #ddd" }}
                      >
                        {expLabel}
                      </TableCell>
                    )}
                    <TableCell sx={{ ...cellStyle, borderRight: "1px solid #ddd" }}>
                      {group.groupName || ""}
                    </TableCell>
                    {colCounts.map((count, ci) => (
                      <TableCell key={strainColumns[ci].id} align="center" sx={{ ...cellStyle, borderRight: "1px solid #ddd" }}>{count}</TableCell>
                    ))}
                    <TableCell align="center" sx={{ ...cellStyle, fontWeight: "bold" }}>
                      {rowTotal}
                    </TableCell>
                  </TableRow>
                );
              });
            })}

            {/* Totals value row */}
            <TableRow sx={pinkStyle}>
              <TableCell colSpan={3} sx={cellStyle} />
              {grandTotals.map((total, ci) => (
                <TableCell key={strainColumns[ci].id} align="center" sx={{ ...cellStyle, fontWeight: "bold", borderRight: "1px solid #ddd" }}>{total}</TableCell>
              ))}
              <TableCell align="center" sx={{ ...cellStyle, fontWeight: "bold" }}>{grandTotalAll}</TableCell>
            </TableRow>

            {/* Totals label row */}
            <TableRow sx={pinkStyle}>
              <TableCell colSpan={3} sx={cellStyle} />
              {strainColumns.map((col) => (
                <TableCell key={col.id} align="center" sx={{ ...cellStyle, fontStyle: "italic", fontSize: "0.78rem", borderRight: "1px solid #ddd" }}>Total {col.label}</TableCell>
              ))}
              <TableCell align="center" sx={{ ...cellStyle, fontStyle: "italic", fontSize: "0.78rem" }}>Total application</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Per-experiment design details */}
      {formData.map((exp, i) => {
        const label = exp.experimentObjective || `Experiment ${i + 1}`;
        const fields = [
          ["Experimental design", exp.experimentalDesign],
          ["Randomisation / Assignment", exp.randomization],
          ["Blinding", exp.blinding],
          ["Inclusion criteria", exp.inclusionCriteria],
          ["Exclusion criteria", exp.exclusionCriteria],
          ["Primary endpoints", exp.primaryEndpoints],
          ["Statistical analysis plan", exp.statisticalAnalysis],
        ].filter(([, v]) => v && String(v).trim());

        if (fields.length === 0) return null;
        return (
          <Box key={i} sx={{ mb: 2 }}>
            <Typography
              sx={{ fontWeight: "bold", color: "#cc0044", mb: 0.5, fontFamily: "inherit" }}
            >
              {label}
            </Typography>
            {fields.map(([fieldLabel, value]) => (
              <DetailLine key={fieldLabel} label={fieldLabel} value={value} />
            ))}
          </Box>
        );
      })}

      <SectionHeading>Section 30: Justification of Animal Numbers</SectionHeading>
      <SectionIntro>
        Explain how the number of animals was justified for each experiment, including the type of experiment, the
        experimental unit, the main factor, the outcome, the expected variability, the design, the measures to avoid
        bias, the sample size method, and the planned statistical test.
      </SectionIntro>
      {formData.map((exp, index) => {
        const summary = buildSection30Summary(exp, index);
        if (!summary.hasContent) return null;
        return (
          <Box key={`section30-${index}`} sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: "bold", color: "#cc0044", mb: 0.5, fontFamily: "inherit" }}>
              {summary.experimentLabel}
            </Typography>
            {summary.lines.map((line, lineIndex) => (
              <Typography
                key={`${summary.experimentLabel}-${lineIndex}`}
                sx={{ fontFamily: '"Times New Roman", serif', fontSize: "0.9rem", mb: 0.45 }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        );
      })}

      <SectionHeading>Section 36: Distribution by degree of severity</SectionHeading>

      <SectionIntro>
        Summary evaluation of the expected maximum stress on the animals. Indicate the expected maximum degree of
        severity for each animal category and group. This preview currently reports the planned animal counts per
        severity degree for each experiment.
      </SectionIntro>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, borderRight: "1px solid #ddd" }}>Objective</TableCell>
              <TableCell sx={{ ...headerCellStyle, borderRight: "1px solid #ddd" }}>Experiment</TableCell>
              {strainColumns.map((col) => (
                <TableCell key={col.id} align="center" sx={{ ...headerCellStyle, borderRight: "1px solid #ddd" }}>{col.label}</TableCell>
              ))}
              <TableCell align="center" sx={{ ...headerCellStyle, borderRight: "1px solid #ddd" }}>Total Experiment</TableCell>
              <TableCell align="center" sx={{ ...headerCellStyle, borderRight: "1px solid #ddd" }}>SD0</TableCell>
              <TableCell align="center" sx={{ ...headerCellStyle, borderRight: "1px solid #ddd" }}>SD1</TableCell>
              <TableCell align="center" sx={{ ...headerCellStyle, borderRight: "1px solid #ddd" }}>SD2</TableCell>
              <TableCell align="center" sx={headerCellStyle}>SD3</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formData.map((exp, expIndex) => {
              const objectiveLabel = exp.objectiveLabel || `Objective ${expIndex + 1}`;
              const objectiveCount = formData.filter(
                (item, idx) => (item.objectiveLabel || `Objective ${idx + 1}`) === objectiveLabel
              ).length;
              const firstObjectiveIndex = formData.findIndex(
                (item, idx) => (item.objectiveLabel || `Objective ${idx + 1}`) === objectiveLabel
              );
              const experimentLabel = exp.experimentObjective || `Experiment ${expIndex + 1}`;
              const groups = exp.groups || [];
              const colCounts = strainColumns.map(({ id }) =>
                groups.reduce((sum, g) => sum + (parseInt(getGroupCount(g, id), 10) || 0), 0)
              );
              const total = colCounts.reduce((a, b) => a + b, 0);
              const distribution = exp.severityDistribution || {};
              const sdTotals = SD_KEYS.map((key) =>
                strainColumns.reduce((sum, { id }) => sum + (parseInt((distribution[key] || {})[id], 10) || 0), 0)
              );

              return (
                <TableRow key={`severity-${expIndex}`}>
                  {expIndex === firstObjectiveIndex && (
                    <TableCell
                      rowSpan={objectiveCount}
                      sx={{ ...cellStyle, color: "#cc0044", fontWeight: "bold", verticalAlign: "top", borderRight: "1px solid #ddd" }}
                    >
                      {objectiveLabel}
                    </TableCell>
                  )}
                  <TableCell sx={{ ...cellStyle, borderRight: "1px solid #ddd" }}>{experimentLabel}</TableCell>
                  {colCounts.map((count, ci) => (
                    <TableCell key={strainColumns[ci].id} align="center" sx={{ ...cellStyle, borderRight: "1px solid #ddd" }}>{count}</TableCell>
                  ))}
                  <TableCell align="center" sx={{ ...cellStyle, fontWeight: "bold", borderRight: "1px solid #ddd" }}>{total}</TableCell>
                  {sdTotals.map((sdTotal, si) => (
                    <TableCell key={SD_KEYS[si]} align="center" sx={{ ...cellStyle, borderRight: si < 3 ? "1px solid #ddd" : undefined }}>{sdTotal}</TableCell>
                  ))}
                </TableRow>
              );
            })}
            <TableRow sx={pinkStyle}>
              <TableCell colSpan={2} sx={cellStyle} />
              {grandTotals.map((total, ci) => (
                <TableCell key={strainColumns[ci].id} align="center" sx={{ ...cellStyle, fontWeight: "bold", borderRight: "1px solid #ddd" }}>{total}</TableCell>
              ))}
              <TableCell align="center" sx={{ ...cellStyle, fontWeight: "bold", borderRight: "1px solid #ddd" }}>{grandTotalAll}</TableCell>
              {SD_KEYS.map((key, si) => (
                <TableCell key={key} align="center" sx={{ ...cellStyle, fontWeight: "bold", borderRight: si < 3 ? "1px solid #ddd" : undefined }}>
                  {severityTotals[key].reduce((a, b) => a + b, 0)}
                </TableCell>
              ))}
            </TableRow>
            <TableRow sx={pinkStyle}>
              <TableCell colSpan={2} sx={cellStyle} />
              {strainColumns.map((col) => (
                <TableCell key={col.id} align="center" sx={{ ...cellStyle, fontStyle: "italic", fontSize: "0.78rem", borderRight: "1px solid #ddd" }}>Total {col.label}</TableCell>
              ))}
              <TableCell align="center" sx={{ ...cellStyle, fontStyle: "italic", fontSize: "0.78rem", borderRight: "1px solid #ddd" }}>Total application</TableCell>
              {SD_KEYS.map((key, si) => (
                <TableCell key={key} align="center" sx={{ ...cellStyle, fontStyle: "italic", fontSize: "0.78rem", borderRight: si < 3 ? "1px solid #ddd" : undefined }}>
                  Total {key.toUpperCase()}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
  );
}

export default DocPreview;
